import { supabase } from '@/integrations/supabase/client';
import * as adapter from '@/lib/supabaseAdapter';

export type VenueType = 'club' | 'live_music' | 'bar_lounge' | 'mixed_use';
export type CapacityBand = 'under_300' | '300_700' | '700_1500' | '1500_plus';
export type Goal =
  | 'book_stronger_artists'
  | 'avoid_bad_dates'
  | 'improve_ticket_sales'
  | 'research_competitors'
  | 'pick_better_pricing'
  | 'plan_smarter_marketing';

export interface VenueProfile {
  id: string;
  user_id: string;
  venue_name: string;
  city: string;
  zip_code: string;
  venue_type: VenueType;
  capacity_band: CapacityBand;
  competitors: string[] | null;
  primary_genres: string[] | null;
  goals: Goal[] | null;
  alerts_enabled: boolean;
  onboarding_completed: boolean;
  latitude: number | null;
  longitude: number | null;
  created_at: string;
  updated_at: string;
}

export interface VenueProfileInput {
  venue_name: string;
  city: string;
  zip_code: string;
  venue_type: VenueType;
  capacity_band: CapacityBand;
  competitors?: string[] | null;
  primary_genres?: string[] | null;
  goals?: Goal[] | null;
  alerts_enabled?: boolean;
  onboarding_completed?: boolean;
  team_report_emails?: string[];
}

export const VENUE_TYPE_LABELS: Record<VenueType, string> = {
  club: 'Club / Nightclub',
  live_music: 'Live Music Venue',
  bar_lounge: 'Bar / Lounge',
  mixed_use: 'Mixed Use',
};

export const CAPACITY_BAND_LABELS: Record<CapacityBand, string> = {
  under_300: 'Under 300',
  '300_700': '300–700',
  '700_1500': '700–1,500',
  '1500_plus': '1,500+',
};

export const GOAL_LABELS: Record<Goal, string> = {
  book_stronger_artists: 'Book stronger artists',
  avoid_bad_dates: 'Avoid bad dates',
  improve_ticket_sales: 'Improve ticket sales',
  research_competitors: 'Research competitors',
  pick_better_pricing: 'Pick better pricing',
  plan_smarter_marketing: 'Plan smarter marketing',
};

export const GENRE_OPTIONS = [
  'House',
  'Techno',
  'EDM',
  'Hip-Hop',
  'R&B',
  'Latin',
  'Pop',
  'Rock',
  'Indie',
  'Jazz',
  'Blues',
  'Country',
  'Metal',
  'Punk',
  'Reggae',
  'Soul',
  'Funk',
  'Other',
];

/**
 * Get the current user's venue profile.
 * First checks if user owns a venue, then checks if user is a team member via user_roles.
 */
export async function getCurrentUserProfile(): Promise<VenueProfile | null> {
  const user = await adapter.getCurrentUser();
  if (!user) return null;

  // First, check if user owns a venue
  const ownedVenue = await adapter.getOwnedVenueProfile(user.id);
  if (ownedVenue) {
    return ownedVenue as VenueProfile;
  }

  // If not an owner, check if user is a team member via user_roles
  const venueId = await adapter.getUserRoleVenueId(user.id);
  if (!venueId) {
    return null;
  }

  // Get the venue profile they have access to
  const teamVenue = await adapter.getVenueProfileById(venueId);
  if (!teamVenue) {
    return null;
  }

  return teamVenue as VenueProfile;
}

/**
 * Check if the current user has any venue access (owner or team member)
 */
export async function hasVenueAccess(): Promise<{
  hasAccess: boolean;
  isOwner: boolean;
  venueId: string | null;
}> {
  const user = await adapter.getCurrentUser();
  if (!user) return { hasAccess: false, isOwner: false, venueId: null };

  // Check if user owns a venue
  const ownedVenue = await adapter.getOwnedVenueProfile(user.id);
  if (ownedVenue) {
    return { hasAccess: true, isOwner: true, venueId: ownedVenue.id };
  }

  // Check if user is a team member via user_roles
  const venueId = await adapter.getUserRoleVenueId(user.id);
  if (venueId) {
    return { hasAccess: true, isOwner: false, venueId };
  }

  return { hasAccess: false, isOwner: false, venueId: null };
}

export async function upsertProfile(
  input: VenueProfileInput,
): Promise<{ success: boolean; venueId?: string; error?: string }> {
  const user = await adapter.getCurrentUser();
  if (!user) return { success: false, error: 'Not authenticated' };

  const { data, error } = await supabase
    .from('venue_profiles')
    .upsert(
      {
        user_id: user.id,
        ...input,
      },
      {
        onConflict: 'user_id',
      },
    )
    .select('id')
    .single();

  if (error) {
    console.error('Error upserting venue profile:', error);
    return { success: false, error: error.message };
  }

  return { success: true, venueId: data?.id };
}

/**
 * Check if onboarding is completed.
 * For team members (non-owners), they skip onboarding entirely.
 */
export async function isOnboardingCompleted(): Promise<boolean> {
  const { hasAccess, isOwner } = await hasVenueAccess();

  // Team members skip onboarding - they're added to an existing venue
  if (hasAccess && !isOwner) {
    return true;
  }

  // Owners need to complete onboarding
  const profile = await getCurrentUserProfile();
  return profile?.onboarding_completed ?? false;
}

export async function updateProfile(
  updates: Partial<VenueProfileInput>,
): Promise<{ success: boolean; error?: string }> {
  try {
    const user = await adapter.getCurrentUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const { data, error } = await supabase
      .from('venue_profiles')
      .update(updates)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      console.error('Error updating venue profile:', error);
      return { success: false, error: error.message };
    }

    if (!data) {
      return { success: false, error: 'No venue profile found for this user' };
    }

    return { success: true };
  } catch (err) {
    console.error('Error updating venue profile:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}
